// Sprint 3 Story 3 — globale Abteilungs-Liste + CRUD-Helper.
//
// GET /api/departments wird einmal beim ersten useDepartments() Aufruf
// gefetcht und im Signal cached. Mutations rufen das API direkt und
// aktualisieren den Cache.

import { signal, type Signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import type { Department } from "@juuplan/shared";
import { api } from "../lib/api";

interface State {
  loading: boolean;
  items: Department[];
  error: string | null;
}

const initial: State = { loading: true, items: [], error: null };
const departmentsSignal: Signal<State> = signal(initial);
let inFlight: Promise<void> | null = null;

async function load(): Promise<void> {
  try {
    const items = await api<Department[]>("/api/departments");
    departmentsSignal.value = { loading: false, items, error: null };
  } catch (err) {
    const msg = err instanceof Error ? err.message : "Unknown error";
    departmentsSignal.value = { loading: false, items: [], error: msg };
  } finally {
    inFlight = null;
  }
}

export function useDepartments(): State {
  useEffect(() => {
    if (!inFlight && departmentsSignal.value.loading) {
      inFlight = load();
    }
  }, []);
  return departmentsSignal.value;
}

export async function refreshDepartments(): Promise<void> {
  inFlight = load();
  return inFlight;
}

export async function createDepartment(name: string): Promise<Department> {
  const dept = await api<Department>("/api/admin/departments", {
    method: "POST",
    body: { name },
  });
  await refreshDepartments();
  return dept;
}

export async function renameDepartment(id: number, name: string): Promise<Department> {
  const dept = await api<Department>(`/api/admin/departments/${id}`, {
    method: "PATCH",
    body: { name },
  });
  await refreshDepartments();
  return dept;
}

export async function deleteDepartment(id: number): Promise<void> {
  await api(`/api/admin/departments/${id}`, { method: "DELETE" });
  await refreshDepartments();
}

export interface DepartmentMember {
  user_id: number;
  name: string;
  email: string;
  is_external: boolean;
  is_active: boolean;
}

export async function listDepartmentMembers(id: number): Promise<DepartmentMember[]> {
  return api<DepartmentMember[]>(`/api/admin/departments/${id}/members`);
}

export async function addDepartmentMember(id: number, user_id: number): Promise<void> {
  await api(`/api/admin/departments/${id}/members`, {
    method: "POST",
    body: { user_id },
  });
}

export async function removeDepartmentMember(id: number, user_id: number): Promise<void> {
  await api(`/api/admin/departments/${id}/members/${user_id}`, {
    method: "DELETE",
  });
}
